import {createElWithAttrs} from '../helpers/born.utils';

/**
 * [createModal creates a modal and body overlay on the fly
 * @param  {[object literal]} options [
 * options to setup your modal
 * 
 * modalID: [string] modal ID, can be space separeted for multiple classes. This class should be unique if you're using keepAlive because the method will open the modal through that class, if there exists more than one match it'll open the first one.
 * 
 * modalContent: [object] element object which contains all the content for your modal. 
 * 
 * wrapper: [string] (optional) specify a custom wrapper for the overlay, defaults to the page's '.wrapper' element.
 * 
 * keepAlive: [boolean] (optional) wether or not to not remove the modal from the DOM after it's closed. Defaults to false.
 * 
 * closeCallback: [function] (optional) callback to run after the modal is closed.
 * 
 * beforeOpenCallback: [function] (optiona) callback to run before the modal is opened.
 * 

 * afterCreateCallback: [function] (optional) callback that runs after the modal is created. If keepAlive is true it will run every time the modal loads.
 * 
 * ]
 * @return {[object]}         [returns overlay element]
 */

export default class Modal {
	constructor(options){
		this.options = options || {};

		//Options
		this.options.closeOthers  = this.options.closeOthers ? true : false;
		this.options.overlayClass = 'window-overlay ' + (this.options.overlayClass || '');
		this.options.modalClass   = 'window-overlay--modal ' + (this.options.modalClass || '');
		this.options.modalID      = this.options.modalID || 'window-overlay-default-id';
		this.options.wrapper      = (typeof this.options.wrapper === 'string' ? document.querySelector(this.options.wrapper) : this.options.wrapper) || document.querySelector('body');
		this.options.isCloseBlocked  = this.options.isCloseBlocked ? true : false;

		//Callbacks
		this._beforeOpenCallback  = this.options.beforeOpenCallback   || function(){};
		this._afterCreateCallback = this.options.afterCreateCallback  || function(){};

		this.overlay = document.querySelector('#modal-' + this.options.modalID);

		this._beforeOpenCallback(this.overlay, this);

		//Close other modals before opening this one if true.
		if(this.options.closeOthers) {
			Modal.closeModal();
		}

		//If overlay doesn't exist create it
		if (!this.overlay) {
			this.overlay = this.renderModal();
		}
		
		if (this.options.update) {
			this.updateModal(this.overlay, this.options.modalContent);
		}

		this._afterCreateCallback(this.overlay, this);

		Modal.openModal(this.overlay);
	}

	/**
	 * creates the modal
	 */
	renderModal() {
		let overlay = createElWithAttrs(false, {'id': 'modal-' + this.options.modalID, 'class': this.options.overlayClass, 'data-modal-id': this.options.modalID}),
			modal   = createElWithAttrs(overlay, {'class': this.options.modalClass});

		if (!this.options.isCloseBlocked) {
			this.insertCloseBtn(modal);
		}

		overlay.modal = {};
		overlay.modal.wrapper    = this.options.wrapper;
		overlay.modal.keepAlive  = this.options.hasOwnProperty('keepAlive') ? this.options.keepAlive : true;
		overlay.modal.afterOpen  = this.options.afterOpenCallback  || function() {};
		overlay.modal.afterClose = this.options.afterCloseCallback || function() {};
		overlay.modal.open       = Modal.openModal.bind(this, overlay);
		overlay.modal.close      = Modal.closeModal;
		overlay.modal.isCloseBlocked   = this.options.isCloseBlocked;

		if (this.options.modalContent) {
			this.insertContent(modal, this.options.modalContent);
		}

		//Using jQuery append cause sometimes the HTML might contain <script> tags
		$(overlay.modal.wrapper).append(overlay);

		//Delay activation a bit to allow for DOM appends to finish, hopefully.
		//This is ugly.
		let checkReady = setInterval(() => {
			if(Modal.exists(this.options.modalID)) {
				clearInterval(checkReady);
			
				overlay.modal.open();
			}
		});

		return overlay;
	}

	/**
	 * Replaces modal's ID and content with the provided values
	 * @param  {[type]} options [description]
	 */
	updateModal(overlay, modalContent, newID) {
		let modal = overlay.querySelector('.window-overlay--modal');

		if (newID) {
			overlay.id = 'modal-' + newID;
			overlay.setAttribute('data-modal-id', newID);
		}

		modal.innerHTML = '';
		this.insertContent(modal, modalContent);

		if (!this.options.isCloseBlocked) {
			this.insertCloseBtn(modal);
		}

	}

	//Inserts close button into modal
	insertCloseBtn(modal) {
		let closeBtn = createElWithAttrs(modal, {'class': 'window-overlay--close', 'data-modal-close': true}, 'span');
	}

	//Adds modal content depending as a string or as a node.
	insertContent(modal, modalContent) {
		if (typeof modalContent === 'string') {
			modal.insertAdjacentHTML('afterbegin', modalContent);
		}

		else {
			modal.appendChild(modalContent);
		}
	}

	/**
	 * Opens referenced modal
	 * @param  {[Node]} overlay [Overlay element to be opened]
	 */
	static openModal(overlay) {
		
		overlay.addEventListener('click', Modal.closeModal);
		document.body.addEventListener('keydown', Modal.closeModal);

		document.documentElement.classList.add('cancel-scroll');
		overlay.classList.add('overlay--active');
		overlay.modal.wrapper.classList.add('overlay--shown');

		//Run this when eerything's in place
		overlay.modal.afterOpen(overlay);
	}

	/**
	 * [closeModal method to... You guessed it, close modals!]
	 * @param  {[object]} e [event]
	 */
	static closeModal(e) {
		let overlay = Modal.getActiveOverlay(),
			canClose = true;

		if(!overlay) {
			return;
		}

		if(typeof e === 'object') {
			let wasClick = e.type === 'click' && (e.target === overlay || e.target.hasAttribute('data-modal-close')),
				wasEsc   = document.activeElement.tagName !== 'INPUT' && e.keyCode === 27;

			canClose = wasClick || wasEsc;
		}

		if (overlay.modal.isCloseBlocked) {
			canClose = false;
		}
		
		if (canClose) {
			overlay.removeEventListener('click', Modal.closeModal);
			document.body.removeEventListener('keydown', Modal.closeModal);

			document.documentElement.classList.remove('cancel-scroll');
			overlay.modal.wrapper.classList.remove('overlay--shown');

			if (overlay.modal.keepAlive) {
				overlay.classList.remove('overlay--active');
			}

			else {
				overlay.parentNode.removeChild(overlay);
			}

			overlay.modal.afterClose(overlay, e);
		}
	}

	/**
	 * Gets the currently active modal
	 * @return {Node}
	 */
	static getActiveOverlay() {
		return document.querySelector('.window-overlay.overlay--active');
	}

	/**
	 * Returns Modal NodeElement if the passed ID matches a modal.
	 * @return {Node}    
	 */
	static exists(modalID) {
		return document.querySelector('#modal-' + modalID);
	}
}


// WEBPACK FOOTER //
// ./cartridges/app_modist_core/cartridge/js/modules/module.modal.js